import type { ChallengeResults, MapSpec, ResultQuestion } from '@cruz/shared';
import { lazy, Suspense, useEffect, useState } from 'react';
import { api, errorMessage } from './api.ts';
import {
  Leaderboard,
  Notice,
  primaryButton,
  Prompt,
  seconds,
  secondaryButton,
  ShareButtons,
} from './ui.tsx';

const RouteMap = lazy(() => import('./RouteMap.tsx'));

/** Score, every answer with the right one and its explanation, and the leaderboard. */
export function ResultsScreen({
  code,
  token,
  creator,
}: {
  code: string;
  token: string;
  creator: string;
}) {
  const [results, setResults] = useState<ChallengeResults | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .results(code, token, { retries: 10 })
      .then((r) => {
        if (r.status === 'ok') setResults(r);
        else setError('Tu partida aún no ha terminado.');
      })
      .catch((err: unknown) => setError(errorMessage(err)));
  }, [code, token]);

  if (error) return <Notice tone="error">{error}</Notice>;
  if (!results) {
    return (
      <p className="py-10 text-center text-lg text-ink-soft dark:text-cream/75">
        Cargando los resultados…
      </p>
    );
  }
  const { me, questions } = results;
  const total = questions.length;
  return (
    <div className="space-y-8">
      <section className="rounded-3xl bg-purple px-5 py-6 text-center text-cream dark:bg-night-800">
        <p className="text-lg text-cream/85">{me.nickname}, has acertado</p>
        <p className="font-serif text-5xl">
          {me.correct} <span className="text-3xl text-cream/80">de {total}</span>
        </p>
        <p className="mt-2 text-base text-cream/85">
          {me.rank != null && `Vas ${me.rank}.º de ${results.leaderboard.length} · `}
          tiempo total {seconds(me.total_ms)}
        </p>
      </section>

      <section>
        <h2 className="mb-3 font-serif text-2xl text-purple dark:text-cream">Clasificación</h2>
        <Leaderboard entries={results.leaderboard} playing={results.playing} me={me.nickname} />
      </section>

      <section>
        <h2 className="mb-3 font-serif text-2xl text-purple dark:text-cream">Tus respuestas</h2>
        <ol className="space-y-4">
          {questions.map((q) => (
            <ResultItem key={q.position} q={q} />
          ))}
        </ol>
      </section>

      <section className="rounded-3xl border-2 border-gold/40 bg-gold-300/20 p-5 dark:border-gold-300/30 dark:bg-gold-300/10">
        <h2 className="font-serif text-2xl">¿Quieres saber más?</h2>
        <p className="mt-1 mb-4 text-lg">
          Horarios, recorridos y por dónde pasa cada cofradía, con los datos oficiales.
        </p>
        <a href="/" className={primaryButton}>
          Pregúntale a Cruz de IA
        </a>
      </section>

      <section className="space-y-3">
        <h2 className="font-serif text-2xl text-purple dark:text-cream">Reta a más amigos</h2>
        <ShareButtons code={code} creator={creator} />
        <a href="/reto/nuevo" className={secondaryButton}>
          Crear otro reto
        </a>
      </section>
    </div>
  );
}

function ResultItem({ q }: { q: ResultQuestion }) {
  const [showOthers, setShowOthers] = useState(false);
  const map = q.extra?.map as MapSpec | undefined;
  const mine = q.choice == null ? null : q.options[q.choice];
  return (
    <li
      className={`rounded-2xl border-2 bg-white/70 p-4 dark:bg-night-800 ${q.correct ? 'border-green-700/40 dark:border-green-400/30' : 'border-burgundy/30 dark:border-burgundy-300/30'}`}
    >
      <p className="text-sm text-ink-soft dark:text-cream/70">
        {q.position + 1}. {q.correct ? '✔ Acierto' : '✘ Fallo'}
        {q.response_ms != null && ` · ${seconds(q.response_ms)}`}
      </p>
      <p className="mt-1 text-lg font-semibold leading-snug">
        <Prompt text={q.prompt} />
      </p>
      {map && (
        <div className="mt-2">
          <Suspense fallback={null}>
            <RouteMap map={map} small />
          </Suspense>
        </div>
      )}
      <p className="mt-2">
        Respuesta correcta: <strong>{q.options[q.correct_choice]}</strong>
      </p>
      {!q.correct && (
        <p className="text-ink-soft dark:text-cream/75">
          Tu respuesta: {mine ?? 'sin responder a tiempo'}
        </p>
      )}
      <p className="mt-2 text-base">{q.explanation}</p>
      <p className="mt-1 text-sm text-ink-soft dark:text-cream/65">
        Fuente: {q.source} · datos de {q.data_year}
      </p>
      {q.others.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            className="text-sm text-purple underline underline-offset-4 dark:text-gold-300"
            aria-expanded={showOthers}
            onClick={() => setShowOthers((s) => !s)}
          >
            {showOthers ? 'Ocultar' : 'Qué respondieron los demás'}
          </button>
          {showOthers && (
            <ul className="mt-1 text-sm">
              {q.others.map((o) => (
                <li key={o.nickname}>
                  {o.correct ? '✔' : '✘'} {o.nickname}: {o.answer ?? 'sin responder'}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}
